<template>
  <div class="wrapper inline-grid grid-cols-9 col-gap width-full">
    <header class="page-title col-span-6 col-start-2">
      <h1 class="header-one">
        Border color
      </h1>
      <p class="page-title__sub">
        Utility classes for border colours.
      </p>
    </header>
    <section class="section col-span-6 col-start-2">
      <p>
        Naming of border color classes will change from project to project,
        depending on the names given to the colors. The example given here are based
        on Jump24's branding.
      </p>
      <p>
        You can find out how to customise the colours for your project in,
        Customisation -> Colours.
      </p>
      <div class="table">
        <table>
          <thead>
            <tr>
              <th>Class name</th>
              <th>Properties</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code class="code__class">.border-selective-yellow</code>
              </td>
              <td>
                <code class="code__prop">border-color: #F2B705;</code>
              </td>
              <td>
                <span class="table__block border-selective-yellow border-solid border-2" />
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-river-bed</code>
              </td>
              <td>
                <code class="code__prop">border-color: #424957;</code>
              </td>
              <td>
                <span class="table__block border-river-bed border-solid border-2" />
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-tiara</code>
              </td>
              <td>
                <code class="code__prop">border-color: #C7D3D6;</code>
              </td>
              <td>
                <span class="table__block border-tiara border-solid border-2" />
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-edward</code>
              </td>
              <td>
                <code class="code__prop">border-color: #AAB0B1;</code>
              </td>
              <td>
                <span class="table__block border-edward border-solid border-2" />
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-porcelain</code>
              </td>
              <td>
                <code class="code__prop">border-color: #EFF1F2;</code>
              </td>
              <td>
                <span class="table__block border-porcelain border-solid border-2" />
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-charcoal</code>
              </td>
              <td>
                <code class="code__prop">border-color: #1D1F1F;</code>
              </td>
              <td>
                <span class="table__block border-charcoal border-solid border-2" />
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-storm-grey</code>
              </td>
              <td>
                <code class="code__prop">border-color: #767B81;</code>
              </td>
              <td>
                <span class="table__block border-storm-grey border-solid border-2" />
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-whisper</code>
              </td>
              <td>
                <code class="code__prop">border-color: #EBEBEB;</code>
              </td>
              <td>
                <span class="table__block border-whisper border-solid border-2" />
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-ghost-white</code>
              </td>
              <td>
                <code class="code__prop">border-color: #FEFEFF;</code>
              </td>
              <td>
                <span class="table__block border-ghost-white border-solid border-2" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Usage
        </h2>
      </header>
      <p>
        To set the border colour of an element, suffix the colour name
        <code>{-name}</code>, for example <code>.border-selective-yellow</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="border-selective-yellow border-solid border-2..."&gt;&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-6 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <div class="col-span-2 col-start-3 m-col-span-4 m-col-start-2 flex flex-col">
              <span class="preview__button border-selective-yellow border-solid border-2" />
            </div>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          RGBA
        </h2>
      </header>
      <p>
        To change the alpha of the border colour, suffix the
        decimal value <code>{-value}</code>, for example
        <code>.border-tiara-9</code>.
      </p>
      <p>
        To set the alpha of the border colour to transparent use
        <code>.border-tiara-0</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="border-tiara-9..."&gt;&lt;/div&gt;
&lt;div class="border-tiara-8..."&gt;&lt;/div&gt;
&lt;div class="border-tiara-7..."&gt;&lt;/div&gt;
&lt;div class="border-tiara-6..."&gt;&lt;/div&gt;
&lt;div class="border-tiara-5..."&gt;&lt;/div&gt;
&lt;div class="border-tiara-4..."&gt;&lt;/div&gt;
&lt;div class="border-tiara-3..."&gt;&lt;/div&gt;
&lt;div class="border-tiara-2..."&gt;&lt;/div&gt;
&lt;div class="border-tiara-1..."&gt;&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-3 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-9</code>
              <span class="preview__button border-2 border-solid border-tiara-9" />
            </div>
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-8</code>
              <span class="preview__button border-2 border-solid border-tiara-8" />
            </div>
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-7</code>
              <span class="preview__button border-2 border-solid border-tiara-7" />
            </div>
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-6</code>
              <span class="preview__button border-2 border-solid border-tiara-6" />
            </div>
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-5</code>
              <span class="preview__button border-2 border-solid border-tiara-5" />
            </div>
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-4</code>
              <span class="preview__button border-2 border-solid border-tiara-4" />
            </div>
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-3</code>
              <span class="preview__button border-2 border-solid border-tiara-3" />
            </div>
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-2</code>
              <span class="preview__button border-2 border-solid border-tiara-2" />
            </div>
            <div class="flex flex-col">
              <code class="code margin-bottom-5">.border-tiara-1</code>
              <span class="preview__button border-2 border-solid border-tiara-1" />
            </div>
          </div>
        </template>
      </preview>
    </section>
  </div>
</template>

<script>
import Preview from '@/components/Preview';
import CodeBlock from '@/components/Code-Block';

export default {
  name: 'BrdColor',
  components: {
    Preview,
    CodeBlock,
  },
};
</script>
